import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { Avatar, Spin } from "antd";
import SkeletonTable from "../../components/table/SkeletonTable";
import recentFilesColumn from "../../columns/dashboard/recentFiles";
import UserAvatar from "../../components/common/UserAvatar";
import { getInitials } from "../../utils/utils";
import { useSelector } from "react-redux";
import { GetAdminDashboardApi } from "../../api/request/dashboard";

const Dashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [licenseData, setLicenseData] = useState([]);
  const [latestUsers, setLatestUsers] = useState([]);

  const getDashboard = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetAdminDashboardApi();
      if (data?.status) {
        setData(data?.data);

        setLicenseData(
          data?.data?.admin_list?.data?.map((item) => {
            return {
              ...item,
              userDetails: <UserAvatar data={item} />,
              businessLogo: (
                <p
                  className="text-blue-500 cursor-pointer hover:underline"
                  onClick={() => window.open(item?.license?.logo, "_blank")}
                >
                  View
                </p>
              ),
              domainName: item?.license?.domain_name,
              licenseName: item?.license?.license_name,
              url: (
                <div className="flex flex-col">
                  <a
                    href={item?.license?.admin_portal_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Admin Portal
                  </a>
                  <a
                    href={item?.license?.client_portal_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Client Portal
                  </a>
                </div>
              ),
              created_at: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "--",
            };
          })
        );
        setLatestUsers(
          data?.data?.activity_overview?.recent_activities_list?.map((item) => {
            return {
              ...item,
              userDetails: <UserAvatar data={item} />,
              created_at: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "--",
            };
          })
        );
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getDashboard();
  }, []);

  const DashboardCard = ({
    title,
    mainValue,
    subStats,
    icon,
    bgColor,
    redirectPath,
  }) => (
    <div
      className="p-4 bg-white border rounded-xl shadow hover:shadow-md transition flex flex-col cursor-pointer"
      onClick={() => navigate(redirectPath)}
    >
      <div className="flex justify-between">
        <p className="text-[18px] font-bold">{title}</p>
        {icon && (
          <div
            className={`${bgColor} rounded-lg size-10 flex items-center justify-center`}
          >
            <img src={icon} alt="icon" className="w-6 h-6" />
          </div>
        )}
      </div>

      {subStats ? (
        <div className="flex justify-between mt-3">
          {subStats?.map((stat, idx) => (
            <div key={idx} className="flex flex-col items-center">
              <p className="text-xl font-semibold text-gray-800">
                {stat?.value}
              </p>
              <p className="text-xs font-medium">{stat?.label}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="card-count mt-3">{mainValue}</p>
      )}
    </div>
  );

  const cards = [
    {
      title: "Total Licenses",
      mainValue: data?.system_overview?.total_admins || 0,
      icon: "/assets/icons/license.svg",
      bgColor: "bg-[#cfcced]",
      redirectPath: "/license",
    },
    {
      title: "Total Clients",
      mainValue: data?.system_overview?.total_clients || 0,
      icon: "/assets/icons/users.svg",
      bgColor: "bg-[#cdd8ec]",
      // redirectPath: "/files_report",
    },
    {
      title: "Total Files",
      mainValue: data?.file_overview?.total_files || 0,
      icon: "/assets/icons/dashboardFile.svg",
      bgColor: "bg-[#DDF6E8]",
      // redirectPath: "/invoice_report",
    },
    {
      title: "Total Services",
      icon: "/assets/icons/services.svg",
      mainValue: data?.total_services || 0,
      bgColor: "bg-[#E5FAFF]",
      // redirectPath: "/signature_report",
    },
    {
      title: "Files",
      icon: "/assets/icons/files.svg",
      bgColor: "bg-[#FEF3C7]",
      // redirectPath: "/payments_report",
      subStats: [
        {
          label: "Unsubmitted",
          value: data?.file_overview?.unsubmitted_files || 0,
        },
        {
          label: "In-process",
          value: data?.file_overview?.in_process_files || 0,
        },
        {
          label: "Closed",
          value: data?.file_overview?.closed_files || 0,
        },
      ],
    },
  ];

  const formatActivityDate = (date) => {
    return moment(date)
      .fromNow()
      .replace("second", "sec")
      .replace("minute", "min");
  };

  const handleFileView = (data) => {
    navigate(`/file/${data?.service_type_id}/${data?.id}`, {
      state: { client: data?.user, task: data?.task },
    });
  };

  const handleClientView = (data) => {
    navigate(`/client_details/${data?.id}`, {
      state: { client: data?.user, task: data?.task },
    });
  };
  const colors = [
    { color: "#00727D", bgColor: "#E5F1F2" }, //theme
    { color: "#7367F0", bgColor: "#E9E7FD" }, //purple
    { color: "#00BAD1", bgColor: "#D6F4F8" }, //blue
    { color: "#FF9F43", bgColor: "#FFF0E1" }, // yellow
    { color: "#28C76F", bgColor: "#DDF6E8" }, // green
    { color: "#FF4C51", bgColor: "#FFE2E3" }, // red
  ];

  const randomColor = useMemo(() => {
    if (!data?.profile_image) {
      const index = Math.floor(Math.random() * colors.length);
      return colors[index];
    }
    return null;
  }, [data?.profile_image]);

  if (!data) return null;

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center flex-col mt-[20%]">
          <Spin size="large" />
          <p className="primary text-center mt-2">Loading...</p>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {cards?.map((card, idx) => (
              <DashboardCard key={idx} {...card} />
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mt-5">
            <div className="md:col-span-2 xl:col-span-3 bg-white border rounded-xl p-4 overflow-x-auto max-h-[600px] overflow-y-auto">
              <div className="flex items-center justify-between sub-title mb-4">
                <h3 className="title">Licenses</h3>
                <Link to="/license" className="text-blue-500">
                  View All
                </Link>
              </div>

              <SkeletonTable
                columns={recentFilesColumn}
                data={licenseData}
                isLoading={isLoading}
                // onRowClick={(data) => handleFileView(data)}
                // index={false}
                // pagination={true}
                stickHeader={true}
                scrollY={400}
              />
            </div>

            <div className="md:col-span-2 xl:col-span-1 bg-white border rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="title">Recent Activity</h3>
                {/* <Link to="/activity" className="text-blue-500">
                  View All
                </Link> */}
              </div>

              <div className="max-h-[500px] overflow-y-scroll scrollbar-hide">
                <div className="sm:space-y-5 space-y-4">
                  {latestUsers?.map((item) => (
                    <div className="flex items-center gap-3">
                      <div>
                        <Avatar
                          size="large"
                          style={{
                            color: !data?.profile_image
                              ? randomColor?.color
                              : "",
                            backgroundColor: !data?.profile_image
                              ? randomColor?.bgColor
                              : "",
                          }}
                          src={item?.user?.profile_image}
                        >
                          {getInitials(item?.user?.name)}
                        </Avatar>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="sub-title capitalize">
                            {item?.user?.name}
                          </p>
                          <p className="text-xs text-Gray text-right">
                            {formatActivityDate(item?.created_at)}
                          </p>
                        </div>

                        <p className="text-Gray text-wrap w-full sm:max-w-full max-w-60">
                          {item?.activity_message}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
